import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  costFormater,
  Enrollment,
  FlexList,
  Heading,
  PencilIcon,
  RawButton,
  Text,
  useResponsive,
} from '@common-fe/common-fe';
import _ from 'lodash';

import { RiskControlOptions } from '@/common/constants';
import AppButton from '@/components/controls/AppButton';
import usePortfolioList from '@/modules/investments/hooks/usePortfolioList';
import RiskCard from '@/modules/investments/RiskCard';
import RiskModal from '@/modules/investments/RiskModal';
import theme from '@/styles/theme';

import { useRiskModels } from '../../hooks';
import { Investment } from '../../investments.types';
import EditAmountModal from '../../modal/EditAmountModal';
import DisclosureFooter from '../DisclosureFooter';
import ProposedFundPreviewModal from '../ProposedFundPreviewModal';
import { useFundingAmountStore } from '../stores/useFundingAmount.store';
import { usePortfolioAllocationStore } from '../stores/usePortfolioAllocation.store';

interface Props {
  onBack: () => void;
  onNext: (value: boolean) => void;
  stepLength: number;
  step: number;
  investorId: string;
  investmentAmount: string;
  currentMyAccount: Enrollment;
  toleranceModel?: string;
}

// const MOCKED_FUNDING_AMOUNT = 500;
const BUTTON_WIDTH = '130px';

const PortfolioAllocation: React.FC<Props> = ({
  onBack,
  onNext,
  stepLength,
  step,
  investorId,
  investmentAmount,
  currentMyAccount,
  toleranceModel,
}) => {
  const { isMobile } = useResponsive();
  const { t } = useTranslation();

  const { riskOptions } = useRiskModels();
  const [currentModalItem, setCurrentModalItem] = useState<Investment | null>(null);
  const [riskValue, setRiskValue] = useState(toleranceModel || RiskControlOptions.MODERATE);
  const { formatedList, data, headers, isLoading, refetch } = usePortfolioList({
    riskValue: riskValue as RiskControlOptions,
    investorId: investorId,
    investmentAmount: _.toNumber(investmentAmount),
    onOpen: (item) => {
      setCurrentModalItem(item);
    },
  });
  const { handleSetValues } = usePortfolioAllocationStore();
  const { handleSetValues: handleSetAmountValues } = useFundingAmountStore();
  const [openRiskModal, setRiskModal] = useState<boolean>(false);
  const [editAmountlVisible, setEditAmountlVisible] = useState<boolean>(false);
  const recommendedRiskObj = useMemo(
    () => riskOptions?.find((elem) => elem.value === riskValue),
    [riskValue, riskOptions]
  );
  const handleSubmit = useCallback(() => {
    onNext(true);
    handleSetValues({
      riskValue,
    });
  }, [onNext, handleSetValues, riskValue]);

  return (
    <Box direction="column" data-testid="portfolio-allocation-wrapper-id">
      {/* <FundsDetailsModal item={foundMobileModalItem} onClose={() => setMobileModalFundId(null)} /> */}
      <Box flex="grow" pad={{ bottom: '40px' }}>
        <Box margin={isMobile ? { horizontal: 'spacing16' } : {}}>
          <Heading
            level={1}
            size={isMobile ? 'mobile' : 'regular'}
            margin={{ bottom: 'spacing12' }}
            data-testid="portfolio-title-wrapper-id"
          >
            {t('Portfolio Allocation')}
          </Heading>

          <Text>
            <Text size="large">{t('Based on your risk assessment, we recommend')}</Text>
            <Text
              size="large"
              margin={{ horizontal: '4px' }}
              weight="bold"
              data-testid="portfolio-allocation_risk-model"
            >
              {t('the ')}
              {recommendedRiskObj?.label}
            </Text>
            <Text size="large">{t('portfolio allocation for you.')}</Text>
          </Text>
        </Box>
        <Box
          flex="grow"
          direction="column"
          background="module"
          round="moduleRound"
          pad={isMobile ? 'spacing16' : 'spacing24'}
          margin={isMobile ? { top: 'spacing8' } : { bottom: 'spacing24', top: 'spacing12' }}
        >
          <Box direction="row" justify="between" margin={isMobile ? {} : { bottom: 'spacing24' }}>
            {!isMobile && (
              <Text size="large" weight="bold">
                {t('Confirm your preferred Investment Risk Model')}
              </Text>
            )}

            <Box direction="row" align="center">
              <Text size="large">
                {isMobile ? t('Amount to be invested:') : t('Estimated amount to be invested:')}
              </Text>
              <RawButton
                data-testid="portfolio-allocation_change-button"
                onClick={() => setEditAmountlVisible(true)}
              >
                <Box direction="row">
                  <Text
                    size="large"
                    weight="bold"
                    color="textAccent"
                    data-testid="portfolio-allocation_button_amount"
                    margin={{ horizontal: 'spacing8' }}
                  >
                    {costFormater(+investmentAmount, true)}
                  </Text>
                  <PencilIcon color={theme.colors.iconAccent} />
                </Box>
              </RawButton>
            </Box>
          </Box>
          <Box direction="column" flex={{ grow: 1 }} width={{ max: '580px' }}>
            {isMobile && (
              <Text size="large" weight={700} margin={{ vertical: 'spacing8' }}>
                {t('Risk model')}
              </Text>
            )}
            <Box direction="row">
              <RiskCard
                riskValue={riskValue}
                setRiskValue={setRiskValue}
                setRiskModal={setRiskModal}
                disabled
                labelColor={theme.colors.stressContainer}
                labelText={t('Your Current Portfolio')}
                isHintHidden
                riskOptions={riskOptions}
              />
            </Box>
          </Box>
        </Box>
        <Box
          background="module"
          data-testid="investment-fund_wrapper"
          round="moduleRound"
          pad={isMobile ? 'spacing16' : undefined}
          margin={isMobile ? { bottom: 'spacing16' } : { bottom: 'spacing24' }}
        >
          <Text
            margin={
              isMobile
                ? { bottom: 'spacing12' }
                : { bottom: 'spacing16', top: 'spacing32', horizontal: 'spacing24' }
            }
            size="large"
            weight="bold"
          >
            {recommendedRiskObj?.label}
            {` Investment Portfolio: ${data?.length || 0}`}
          </Text>
          <ProposedFundPreviewModal item={currentModalItem} onClose={() => setCurrentModalItem(null)} />
          <FlexList
            testId="investment-fund"
            pad={isMobile ? '0px' : undefined}
            rows={formatedList}
            moreCount={isMobile ? data?.length : undefined}
            headers={isMobile ? [] : headers}
            loading={isLoading}
            footer={
              isMobile ? (
                <Box
                  direction="row"
                  justify="between"
                  pad="spacing12"
                  margin={{ top: 'spacing8' }}
                  background="border1"
                  round="container2Round"
                >
                  <Text size="large" weight="bold">
                    {t('Amount to sell')}
                  </Text>

                  <Text size="large" weight="bold" data-testId="portfolio-allocation_footer_amount">
                    {costFormater(+investmentAmount, true)}
                  </Text>
                </Box>
              ) : (
                <Box
                  direction="row"
                  justify="stretch"
                  pad={{ horizontal: 'spacing24', vertical: 'spacing6' }}
                  margin={{ bottom: 'spacing16', top: 'spacing8' }}
                  background="border1"
                  round="container2Round"
                >
                  <Text style={{ flex: 4 }} weight="bold">
                    {t('Total:')}
                  </Text>
                  <Text style={{ flex: 1 }} />
                  <Text weight="bold" data-testId="portfolio-allocation_footer_percents" style={{ flex: 1 }}>
                    100%
                  </Text>
                  <Text weight="bold" data-testId="portfolio-allocation_footer_amount" style={{ flex: 1 }}>
                    {costFormater(+investmentAmount, true)}
                  </Text>
                  <Text style={{ flex: 2 }} />
                </Box>
              )
            }
          />
        </Box>
        <Box direction="column" pad={isMobile ? { horizontal: 'spacing16' } : {}}>
          <Box direction="row" align="center" justify="end">
            <Box direction="row" gap="spacing12" fill={isMobile} justify="end" align="center">
              <Box fill={isMobile}>
                <AppButton
                  fill={isMobile}
                  buttonType="secondary"
                  testId="portfolio-allocation_cancel"
                  onClick={() => onBack()}
                  width={BUTTON_WIDTH}
                >
                  {t('Back')}
                </AppButton>
              </Box>
              <Box fill={isMobile}>
                <AppButton
                  fill={isMobile}
                  testId="portfolio-allocation_submit"
                  type="submit"
                  onClick={handleSubmit}
                  width={BUTTON_WIDTH}
                >
                  {t(`Submit (${step}/${stepLength})`)}
                </AppButton>
              </Box>
            </Box>
          </Box>
          <DisclosureFooter />
        </Box>
      </Box>
      <EditAmountModal
        visible={editAmountlVisible}
        onSetVisible={setEditAmountlVisible}
        currentMyAccount={currentMyAccount}
        investmentAmount={investmentAmount}
        onSave={handleSetAmountValues}
        onRefetch={refetch}
        buySellMode
      />
      <RiskModal
        visible={openRiskModal}
        onSetVisible={(value) => setRiskModal(value)}
        activeRisk={riskValue}
      />
    </Box>
  );
};

export default PortfolioAllocation;
